import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface FieldWrapperProps {
  /**
   * Label text rendered above the control as a `<label>`. Turns destructive
   * when `error` is set. Omit entirely to render no label.
   */
  label?: React.ReactNode;

  /**
   * Hint text rendered below the control — muted, smaller text. Hidden when
   * `error` is set (error takes the slot).
   */
  hint?: React.ReactNode;

  /**
   * Validation error message. When set, replaces `hint`, renders in destructive
   * colour, and injects `aria-invalid={true}` onto the control via Radix Slot.
   */
  error?: React.ReactNode;

  /**
   * Appends a required asterisk (`*`) to the label. Purely visual — also pass
   * `required` to the control itself for native form validation.
   */
  required?: boolean;

  /**
   * Stable `id` for the control. When omitted, an id is auto-generated.
   * Use this when you also render a separate `<label htmlFor>` outside the
   * wrapper, or when you need a predictable id for tests.
   */
  htmlFor?: string;

  /**
   * Controls how the label is positioned relative to the control.
   *
   * - `'default'`     — label rendered above the control with a gap (standard).
   * - `'overlapping'` — label floats over the top border of the control, notching
   *                     through it. The control must have a visible border.
   * - `'inset'`       — label rendered inside the control's border box at the top.
   *                     The control's own border/bg/shadow are stripped so the
   *                     wrapper provides the single styled container.
   */
  labelVariant?: 'default' | 'overlapping' | 'inset';

  /** Additional CSS classes merged onto the root `<div>`. */
  className?: string;

  /** The form control to render. Must accept standard HTML input props. */
  children: React.ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Standalone label + hint + error wrapper for any form control.
 *
 * Uses Radix `Slot` to automatically inject `id`, `aria-invalid`, and
 * `aria-describedby` onto the child — no manual wiring required.
 *
 * Does **not** depend on react-hook-form. Use this for simple controlled
 * fields, Storybook stories, or any context where the full
 * `Form / FormField / FormItem / FormControl / FormMessage` stack is
 * unnecessary.
 *
 * For react-hook-form forms use `FormItem + FormLabel + FormControl +
 * FormDescription + FormMessage` from `form.tsx` instead — they read
 * validation state from the RHF context automatically.
 *
 * @example
 * // Basic
 * <FieldWrapper label="Email" hint="We'll never share it." required>
 *   <Input type="email" placeholder="you@example.com" />
 * </FieldWrapper>
 *
 * @example
 * // With error
 * <FieldWrapper label="Username" error="Username is already taken.">
 *   <Input value={username} onChange={(e) => setUsername(e.target.value)} />
 * </FieldWrapper>
 *
 * @example
 * // No label (aria-label on the control instead)
 * <FieldWrapper error={errors.search}>
 *   <Input aria-label="Search" startIcon={<Search />} />
 * </FieldWrapper>
 */
const FieldWrapper = React.forwardRef<HTMLDivElement, FieldWrapperProps>(
  (
    {
      label,
      hint,
      error,
      required = false,
      htmlFor,
      labelVariant = 'default',
      className,
      children,
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const controlId = htmlFor ?? generatedId;
    const hintId = hint ? `${controlId}-hint` : undefined;
    const errorId = error ? `${controlId}-error` : undefined;

    const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

    const labelEl =
      label !== undefined ? (
        <label
          htmlFor={controlId}
          className={cn(
            'font-medium leading-none',
            labelVariant === 'inset' ? 'text-sm' : 'text-sm',
            labelVariant === 'overlapping'
              ? 'absolute left-3 top-0 z-10 -translate-y-1/2 bg-background px-1 text-xs'
              : labelVariant === 'inset'
                ? 'px-3 pt-2.5 text-xs'
                : '',
            error ? 'text-destructive' : 'text-foreground',
          )}
        >
          {label}
          {required && (
            <span className="ml-0.5 text-destructive" aria-hidden="true">
              *
            </span>
          )}
        </label>
      ) : null;

    const control = (
      <Slot id={controlId} aria-invalid={error ? true : undefined} aria-describedby={describedBy}>
        {children}
      </Slot>
    );

    const footer = (
      <>
        {hint && !error && (
          <p id={hintId} className="text-xs text-muted-foreground">
            {hint}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            role="alert"
            aria-live="polite"
            className="text-xs font-medium text-destructive"
          >
            {error}
          </p>
        )}
      </>
    );

    // ── Overlapping — label floats on the top border ───────────────────────────
    if (labelVariant === 'overlapping') {
      return (
        <div ref={ref} className={cn('space-y-1.5', className)}>
          <div className="relative pt-2">
            {labelEl}
            {control}
          </div>
          {footer}
        </div>
      );
    }

    // ── Inset — label lives inside the border box ──────────────────────────────
    if (labelVariant === 'inset') {
      return (
        <div ref={ref} className={cn('space-y-1.5', className)}>
          <div
            className={cn(
              'rounded-[var(--field-radius,var(--radius))] border border-input bg-input-bg shadow-sm',
              // Strip the wrapped input's own border/bg so the wrapper is the
              // single styled container.
              '[&_input]:border-transparent [&_input]:bg-transparent [&_input]:shadow-none',
            )}
          >
            {labelEl}
            {control}
          </div>
          {footer}
        </div>
      );
    }

    // ── Default — label above, separated ──────────────────────────────────────
    return (
      <div ref={ref} className={cn('space-y-2', className)}>
        {labelEl}
        {control}
        {footer}
      </div>
    );
  },
);
FieldWrapper.displayName = 'FieldWrapper';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { FieldWrapper };
